import { useEffect, useState } from 'react';
import { api, type ScenarioDetail, type ScenarioListRow } from '../lib/api';
import { useUncalibrated } from '../lib/calibration';
import { UncalibratedBadge } from '../components/UncalibratedBadge';

const LOCALE = 'de-AT';

function pickTitle(t: Record<string, string> | null | undefined): string {
  if (!t) return '–';
  return t[LOCALE] ?? t['en'] ?? Object.values(t)[0] ?? '–';
}

interface Props {
  onCancel: () => void;
  /**
   * Optional pick callback. When set, each scenario row gets a
   * "Hinzufügen" CTA that calls back with the scenario id; when
   * unset the picker is read-only (browse the catalogue without
   * applying anything).
   */
  onPicked?: (scenarioId: string) => void | Promise<void>;
  /** Disable the pick CTA while a parent operation is running. */
  picking?: boolean;
  /** Session's range: marks drills whose target has no camera calibration there. */
  locationId?: string | null;
}

/**
 * Scenario catalogue browser for the referee PWA. Lists every non-
 * retired scenario and shows its constituent drills on tap. With
 * `onPicked` set, the referee can append a scenario's stages to the
 * current session (scenario expansion); without it the picker is
 * read-only.
 */
export function ScenarioPicker({ onCancel, onPicked, picking, locationId }: Props) {
  const isUncalibrated = useUncalibrated(locationId);
  const [scenarios, setScenarios] = useState<ScenarioListRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [detail, setDetail] = useState<ScenarioDetail | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    api
      .scenarios()
      .then((rows) => {
        if (!cancelled) setScenarios(rows);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!openId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    api
      .scenarioById(openId)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [openId]);

  const q = filter.toLowerCase();
  const filtered = (scenarios ?? []).filter((r) => {
    if (q === '') return true;
    return (
      r.scenario.code.toLowerCase().includes(q) ||
      pickTitle(r.scenario.titleI18n).toLowerCase().includes(q)
    );
  });

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <header className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Szenarien</h2>
        <button
          type="button"
          onClick={onCancel}
          className="min-h-12 rounded border border-zinc-700 px-3 py-1 text-sm text-zinc-300 hover:border-zinc-500"
        >
          Schließen
        </button>
      </header>

      <input
        type="text"
        placeholder="Suchen…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        className="min-h-12 rounded border border-zinc-700 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none"
      />

      {error && <p className="text-sm text-red-300">{error}</p>}

      {scenarios == null ? (
        <p className="text-sm text-zinc-500">Lade Szenarien…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-zinc-500">Keine Szenarien gefunden.</p>
      ) : (
        <ul className="flex-1 divide-y divide-zinc-800 overflow-y-auto rounded border border-zinc-800">
          {filtered.map((r) => (
            <li key={r.scenario.id} className="flex flex-col">
              <button
                type="button"
                onClick={() => setOpenId(openId === r.scenario.id ? null : r.scenario.id)}
                className="flex min-h-12 items-center justify-between gap-3 px-3 py-3 text-left hover:bg-zinc-800"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{pickTitle(r.scenario.titleI18n)}</p>
                  <p className="font-mono text-xs text-zinc-500">{r.scenario.code}</p>
                </div>
                <span className="shrink-0 text-xs tabular-nums text-zinc-500">
                  {r.scenario.minShots === r.scenario.maxShots
                    ? `${r.scenario.minShots} Schuss`
                    : `${r.scenario.minShots}–${r.scenario.maxShots}`}
                  {r.scenario.parSeconds && <> · PAR {r.scenario.parSeconds} s</>}
                </span>
              </button>
              {openId === r.scenario.id && detail && detail.scenario.id === r.scenario.id && (
                <div className="border-t border-zinc-800 bg-zinc-800/50 px-3 py-3">
                  <p className="mb-2 text-xs whitespace-pre-line text-zinc-300">
                    {detail.scenario.descriptionMd}
                  </p>
                  <p className="text-xs font-semibold text-zinc-500">Übungen</p>
                  <ol className="mt-1 list-decimal pl-5 text-xs">
                    {detail.drills.map((s) => (
                      <li key={s.slot.id}>
                        {s.slot.nameOverride ?? s.drillName}
                        <span className="ml-1 text-zinc-500">
                          · {s.targetName}
                          {s.slot.distanceCm != null && (
                            <> · {(s.slot.distanceCm / 100).toLocaleString(LOCALE)} m</>
                          )}
                          · {s.slot.scoringMode === 'speed' ? 'Speed' : 'Präzision'}
                        </span>
                        {isUncalibrated(s.targetTypeId) && (
                          <span className="ml-2 align-middle">
                            <UncalibratedBadge compact />
                          </span>
                        )}
                      </li>
                    ))}
                  </ol>
                  {onPicked && (
                    <button
                      type="button"
                      disabled={picking}
                      onClick={() => onPicked(r.scenario.id)}
                      className="mt-3 min-h-12 rounded bg-emerald-700 px-3 py-2 text-sm font-bold text-white hover:bg-emerald-600 disabled:opacity-60"
                    >
                      {picking ? 'Wird angelegt…' : 'Szenario als Stages anlegen'}
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
